import type { Asset } from '@/services/assets/assetsApi'
import type { CreativeInput } from '@/services/creatives/creativesApi'
import type { Layout, LayoutComponent } from '@/services/sites/layoutsApi'
import { withScheme } from '@/utils/url'

/** What a bulk upload makes — picked first, it decides the files and layouts on offer. */
export type CreativeKind = 'IMAGE' | 'VIDEO' | 'THIRD_PARTY' | 'NATIVE'

export const CREATIVE_KINDS: CreativeKind[] = ['IMAGE', 'VIDEO', 'THIRD_PARTY', 'NATIVE']

/** The file picker's `accept` per kind. Third party takes an ad server's trafficking sheet. */
export const KIND_ACCEPT: Record<CreativeKind, string> = {
  IMAGE: 'image/*',
  VIDEO: 'video/*',
  NATIVE: 'image/*,video/*',
  THIRD_PARTY: '.xls,.xlsx,.csv,.txt',
}

/** Mime types arrive as the API's enum ("IMAGE_PNG") or as a MIME type ("image/png"). */
function isImageMime(mimeType: string): boolean {
  return mimeType.startsWith('IMAGE_') || mimeType.startsWith('image/')
}

function isVideoMime(mimeType: string): boolean {
  return mimeType.startsWith('VIDEO_') || mimeType.startsWith('video/')
}

/** "image/png" → "IMAGE_PNG", so both spellings compare equal. */
function mimeEnum(mimeType: string): string {
  return mimeType.includes('/') ? mimeType.replace(/[/+.-]/g, '_').toUpperCase() : mimeType
}

function assetComponents(layout: Layout): LayoutComponent[] {
  return layout.layoutComponents.filter((c) => c.type === 'ASSET')
}

/**
 * The upload kinds a layout is offered under — possibly more than one, e.g. a
 * native layout with a video slot is both native and video. Third party and
 * native go by layout type. Video goes by the asset slots alone, whatever the
 * layout type: any slot that takes a video file makes it a video layout (VAST
 * included). Image is a standard layout with image slots and no video one.
 * A layout without a type is a standard one — that is the server's default.
 */
export function layoutKinds(layout: Layout): CreativeKind[] {
  const type = layout.layoutType || 'STANDARD'
  if (type === 'THIRD_PARTY') return ['THIRD_PARTY']
  const mimeTypes = assetComponents(layout).flatMap((c) => c.mimeTypes ?? [])
  const video = mimeTypes.some(isVideoMime)
  const kinds: CreativeKind[] = []
  if (type === 'NATIVE') kinds.push('NATIVE')
  if (video) kinds.push('VIDEO')
  if (type === 'STANDARD' && !video && mimeTypes.some(isImageMime)) kinds.push('IMAGE')
  return kinds
}

/**
 * The category a layout is listed under: the one an admin gave it, otherwise
 * the one the server detected. The same rule as `GET /layouts/categories`.
 * Detected categories are prefixed so they never collide with a free-text one.
 */
export function layoutCategoryKey(layout: Layout): string {
  if (layout.layoutCategory) return layout.layoutCategory
  return `detected:${layout.detectedCategory || 'OTHER'}`
}

/** Distinct categories across the layouts, in the order the layouts come. */
export function layoutCategories(layouts: Layout[]): string[] {
  return [...new Set(layouts.map(layoutCategoryKey))]
}

/** The first asset slot that takes the asset — by mime type, then by file size. */
export function assetComponentFor(layout: Layout, asset: Asset): LayoutComponent | undefined {
  return assetComponents(layout).find(
    (c) =>
      (!c.mimeTypes?.length || c.mimeTypes.map(mimeEnum).includes(mimeEnum(asset.mimeType))) &&
      (!c.maxFileSizeBytes || !asset.fileSize || asset.fileSize <= c.maxFileSizeBytes)
  )
}

/** Where a third-party layout takes its tag: the `thirdParty` slot, else its first code field. */
export function codeComponentOf(layout: Layout): LayoutComponent | undefined {
  const texts = layout.layoutComponents.filter((c) => c.type === 'TEXT')
  return texts.find((c) => c.tag === 'thirdParty') ?? texts.find((c) => c.textType === 'CODE')
}

/** The slot the bulk "link" fills — the layout's first URL component. */
export function urlComponentOf(layout: Layout): LayoutComponent | undefined {
  return layout.layoutComponents.find((c) => c.type === 'URL')
}

/** One file (or one tag of a third-party sheet) on its way to becoming a creative. */
export interface BulkCreativeDraft {
  /** Also the id the creative is created at. */
  id: string
  fileName: string
  status: 'uploading' | 'ready' | 'failed'
  /** Upload progress, 0–100. */
  progress: number
  error?: string
  /** The uploaded file, for image, video and native creatives. */
  asset?: Asset
  /** The ad tag, for third-party creatives. */
  tag?: string
  name: string
  /** The file's own size — what the creative gets unless it is shown at 2×. */
  width: number
  height: number
  /**
   * Shows the file at half its size, for screens with twice the pixel density:
   * a 600×500 image makes a 300×250 creative.
   */
  halfSize: boolean
  /** A `layoutCategoryKey`, or '' before one is picked. */
  category: string
  layoutId: string
  url: string
}

/** Whether the draft has a file to scale — images and video, not ad tags. */
export function canHalveSize(draft: BulkCreativeDraft): boolean {
  return !!draft.asset && draft.width > 0 && draft.height > 0
}

/** The size the creative is made at: the file's own, or half of it at 2×. */
export function creativeSize(draft: BulkCreativeDraft): { width: number; height: number } {
  if (!draft.halfSize || !canHalveSize(draft)) return { width: draft.width, height: draft.height }
  return { width: Math.round(draft.width / 2), height: Math.round(draft.height / 2) }
}

/** The name a file gives its creative — the file name without its extension. */
export function nameFromFileName(fileName: string): string {
  const dot = fileName.lastIndexOf('.')
  return dot > 0 ? fileName.slice(0, dot) : fileName
}

/** Whether the layout can carry the draft's file or tag. */
export function layoutFits(layout: Layout, draft: BulkCreativeDraft): boolean {
  if (draft.tag !== undefined) return !!codeComponentOf(layout)
  return !!draft.asset && !!assetComponentFor(layout, draft.asset)
}

/**
 * The layout a draft starts on: the first that takes it, inside the draft's
 * category when it has one.
 */
export function suggestLayout(layouts: Layout[], draft: BulkCreativeDraft): Layout | undefined {
  const fitting = layouts.filter((l) => layoutFits(l, draft))
  return (
    (draft.category && fitting.find((l) => layoutCategoryKey(l) === draft.category)) || fitting[0]
  )
}

/**
 * Fills a name pattern for one draft. `{file}` is the file name, `{n}` the
 * draft's position among those being renamed, `{width}`/`{height}` the
 * creative's size.
 */
export function applyNamePattern(pattern: string, draft: BulkCreativeDraft, n: number): string {
  const { width, height } = creativeSize(draft)
  return pattern
    .replaceAll('{file}', nameFromFileName(draft.fileName))
    .replaceAll('{n}', String(n))
    .replaceAll('{width}', String(width || ''))
    .replaceAll('{height}', String(height || ''))
    .trim()
}

/** What stops a draft being created, if anything. */
export type DraftProblem = 'uploading' | 'failed' | 'noName' | 'noLayout' | 'layoutDoesNotFit'

export function draftProblem(
  draft: BulkCreativeDraft,
  layout: Layout | undefined
): DraftProblem | undefined {
  if (draft.status === 'uploading') return 'uploading'
  if (draft.status === 'failed') return 'failed'
  if (!draft.name.trim()) return 'noName'
  if (!layout) return 'noLayout'
  if (!layoutFits(layout, draft)) return 'layoutDoesNotFit'
  return undefined
}

/**
 * The layout's mandatory fields the upload leaves empty — headlines and the
 * like, which are filled in on the creative afterwards. The creative is still
 * created, only with a validation warning until they are.
 */
export function unfilledComponents(layout: Layout, draft: BulkCreativeDraft): LayoutComponent[] {
  const filled = new Set(Object.keys(layoutParametersFor(layout, draft)))
  return layout.layoutComponents.filter(
    (c) => c.required === 'MANDATORY' && !filled.has(c.tag) && !c.defaultValue
  )
}

function layoutParametersFor(
  layout: Layout,
  draft: BulkCreativeDraft
): Record<string, string | { id: string }> {
  const params: Record<string, string | { id: string }> = {}
  if (draft.asset) {
    const slot = assetComponentFor(layout, draft.asset)
    if (slot) params[slot.tag] = { id: draft.asset.id }
  }
  if (draft.tag !== undefined) {
    const slot = codeComponentOf(layout)
    if (slot) params[slot.tag] = draft.tag
  }
  // A link typed without its scheme would fail the URL component's validation.
  const url = withScheme(draft.url)
  const urlSlot = urlComponentOf(layout)
  if (url && urlSlot) params[urlSlot.tag] = url
  return params
}

/** Who a bulk upload's creatives belong to. */
export type BulkCreativeOwner = { lineItem: string } | { advertiser: string }

/** The create payload for one draft on its layout. */
export function creativeInputFor(
  draft: BulkCreativeDraft,
  layout: Layout,
  owner: BulkCreativeOwner
): CreativeInput & { id: string } {
  const { width, height } = creativeSize(draft)
  return {
    id: draft.id,
    name: draft.name.trim(),
    objectState: 'ACTIVE',
    layout: layout.id,
    layoutParameters: layoutParametersFor(layout, draft),
    width: width || undefined,
    height: height || undefined,
    ...owner,
  }
}

/** Whether a size is one the marketplace product allows; an empty list allows any. */
export function dimensionsAllowed(allowed: number[][], width: number, height: number): boolean {
  return !allowed.length || allowed.some(([w, h]) => w === width && h === height)
}
